import { initAbTesting } from "@solvari/ab-testing";
import { initArgus } from "@solvari/argus/service";
import { localeToIso } from "@solvari/translations";
import { getEnv, resolveClientId } from "@solvari/utils";

import { moveJumplinks } from "@/scripts/utils/jumplinkFixer.ts";

import { initBase } from "./base";

/**
 * The boot sequence every expert-site entry runs: the AB-testing topology
 * (`apps/main-site/CLAUDE.md` § "AB testing: one topology, two implementations") plus the shared
 * base. Importing this module runs nothing — the entry files keep the side effects they own,
 * their stylesheet, their components and the call that starts this.
 *
 * What varies per site is only what surrounds AB testing, so that is all the options describe.
 */

type ExpertSiteOptions = {
  /** After AB testing settled, for anything that must observe the final markup. */
  afterAbTesting?: () => void;
  /** Before AB testing reads the page. Defaults to what every site but solvari needs. */
  beforeAbTesting?: () => void;
  /** Merged over the attributes every expert site sends. */
  siteAttributes?: { hasJumpLinks?: boolean };
};

async function initExpertSite({
  afterAbTesting,
  beforeAbTesting = moveJumplinks,
  siteAttributes,
}: ExpertSiteOptions = {}) {
  const clientId = resolveClientId();
  initArgus(clientId);
  initBase();
  beforeAbTesting();

  await initAbTesting(clientId, {
    expertSiteTheme: getEnv().config.themeId,
    siteLocale: localeToIso(getEnv().config.localeCode),
    siteType: "expert",
    ...siteAttributes,
  });

  afterAbTesting?.();
}

export type { ExpertSiteOptions };
export { initExpertSite };
